import { Grid, Box, Paper, Typography, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton } from '@mui/material';
import LogoSpinner from '../../components/LogoSpinner/LogoSpinner';
import { LineChart } from '@mui/x-charts/LineChart';
import ChordDiagram from "./chordDiagram";
import EventsApi from "../../api/eventsApi/eventsApi";
import {useEffect, useState} from "react"
import { ArrowBackIosNew, ArrowForwardIos } from '@mui/icons-material';
import { format } from 'date-fns';
import { desiredCities } from '../../constants/consts';
import { reportUi } from '../reports/reportUiTheme';


interface Pin {
    id: string;
    name: string;
    sector: string;
    latitude: number;
    longitude: number;
    radiusInMeters: number;
    radius?: number;
    reportDescription?: string;
}

interface PinResponse {
    items: Pin[];
}


const eventTypeColors: Record<string, string> = {
    'Terörizm (TE)': '#D76E3B',
    'Örgütlü Suçlar (OS)': '#F35E5E',
    'Sosyal ve Politik İstikrarsızlık (SP)': '#67C086',
    'Çevresel ve Doğal Afetler (CA)': '#689D7B',
    'Bilgi Güvenliği ve Enformasyon Tehditleri (BG)': '#63ABC3',
    'Bireysel ve Adi Suçlar (BA)': '#4995DB',
    'Göç ve Mülteci Krizleri (GM)': '#8e6c58',
    'Sağlık ve Biyolojik Tehditler (SB)': '#265454',
    'Yasal ve Düzenleyici Uyumsuzluklar (YD)': '#0f223a',
    'Ekonomik ve Finansal Tehditler (EF)': '#4a3b39'
};

// Özel Legend Komponenti
const CustomLegend = ({ series }: { series: { label: string; }[] }) => (
    <Stack 
        direction="row" 
        spacing={0.8}
        sx={{ 
            flexWrap: 'wrap', 
            justifyContent: 'center',
            gap: 0.4,
            mb: 0.8
        }}
    >
        {series.map((item) => (
            <Stack 
                key={item.label} 
                direction="row" 
                alignItems="center" 
                spacing={0.4}
            >
                <Box
                    sx={{
                        width: 6,
                        height: 6,
                        backgroundColor: eventTypeColors[item.label] || '#000',
                        borderRadius: '50%'
                    }}
                />
                <Typography variant="caption" sx={{ fontSize: '0.56rem', color: reportUi.textMuted }}>
                    {item.label}
                </Typography>
            </Stack>
        ))}
    </Stack>
);

interface WeeklyEventTypeCount {
    eventType: string;
    dailyCounts: { [key: string]: number }; // key: "Pazartesi", "Salı" vb.
}

// Interface'leri ekleyelim
interface EventCountsResponse {
    events: {
        items: Array<{
            date: string;
            eventType: {
                id: string;
                name: string;
            };
        }>;
    };
    eventTypeCounts: Array<{
        eventType: string;
        count: number;
    }>;
    averageDailyCount: number;
}

// Add new interface for risk data
interface PinRiskData {
    [pinId: string]: number | null;
}

// Korelasyon verisi için interface ekleyelim
interface CorrelationData {
    [key: string]: { [key: string]: number };
}

// Update formatDateRange function to always show full weeks
const formatDateRange = (startDate: Date, showCurrentWeek: boolean = true) => {
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6); // Always show full week
    
    return `(${startDate.toLocaleDateString('tr-TR', { 
        day: 'numeric',
        month: 'long'
    })} - ${endDate.toLocaleDateString('tr-TR', { 
        day: 'numeric',
        month: 'long'
    })})`;
};

// Response tipi için interface'i güncelleyelim
interface EventResponse {
    events: {
        items: Array<{
            city: string;
            description: string;
        }>;
    };
}

const findCitiesInDescription = (description: string, cities: string[]): string[] => {
    // Tüm eşleşen şehirleri bul
    const foundCities = cities.filter(city => 
        description.toLowerCase().includes(city.toLowerCase())
    );
    
    // Tekrar eden şehirleri temizle
    return Array.from(new Set(foundCities));
};

const createCorrelationMatrix = (events: Array<{city: string, description: string}>) => {
    const correlationData: { [key: string]: { [key: string]: number } } = {};
    const cityMentions: { [key: string]: number } = {};
    
    // Initialize matrices
    desiredCities.forEach(city1 => {
        correlationData[city1] = {};
        cityMentions[city1] = 0;
        desiredCities.forEach(city2 => {
            correlationData[city1][city2] = 0;
        });
    });

    // Count directed relationships
    events.forEach(event => {
        if (!event.city || !event.description) return;
        
        // Sadece source city'nin kendi olaylarında diğer şehirlerin geçme sayısını say
        if (desiredCities.includes(event.city)) {
            const mentionedCities = findCitiesInDescription(event.description, desiredCities);
            
            // Her bir bahsedilen şehir için ilişkiyi kaydet
            mentionedCities.forEach(mentionedCity => {
                if (mentionedCity !== event.city) {
                    correlationData[event.city][mentionedCity]++;
                }
            });

            // Source city'nin toplam olay sayısını güncelle
            cityMentions[event.city]++;
        }
    });

    // Normalize relationships
    desiredCities.forEach(sourceCity => {
        desiredCities.forEach(targetCity => {
            if (sourceCity !== targetCity && cityMentions[sourceCity] > 0) {
                correlationData[sourceCity][targetCity] = 
                    correlationData[sourceCity][targetCity] / cityMentions[sourceCity];
            }
        });
    });

    return correlationData;
};

const Analysis = () => {

    const [isLoading, setIsLoading] = useState(true);
    const [pins, setPins] = useState<Pin[]>([]);

    // Yeni state sadece sol üst grafik için
    const [weeklyEventData, setWeeklyEventData] = useState<{
        series: { data: number[]; label: string; }[];
        xAxis: string[];
    }>({ series: [], xAxis: [] });

    const [weeklyEventTypeCounts, setWeeklyEventTypeCounts] = useState<WeeklyEventTypeCount[]>([]);

    // Add new state for risk levels
    const [pinRiskLevels, setPinRiskLevels] = useState<PinRiskData>({});

    // Add new state for week offset
    const [weekOffset, setWeekOffset] = useState(0);
    const MAX_WEEK_OFFSET = 4; // Maximum number of weeks to go back

    // In the Analysis component, add new state for date range
    const [currentDateRange, setCurrentDateRange] = useState('');
    const [currentWeekDays, setCurrentWeekDays] = useState<{
        name: string;
        date: string;
        displayDate: string;
    }[]>([]);

    // Add new state for correlation data
    const [correlationData, setCorrelationData] = useState<CorrelationData>({});

    // Update getCurrentWeekDays function
    const getCurrentWeekDays = (offset: number = 0) => {
        const days = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
        const today = new Date();
        const currentDay = today.getDay();
        const monday = new Date(today);
        
        // Get to current week's Monday
        monday.setDate(today.getDate() - (currentDay === 0 ? 6 : currentDay - 1)); // Remove the -7
        monday.setDate(monday.getDate() - (offset * 7)); // Apply offset
        
        return {
            days: days.map((day, index) => {
                const date = new Date(monday);
                date.setDate(monday.getDate() + index);
                
                return {
                    name: day,
                    date: date.toISOString().split('T')[0],
                    displayDate: date.toLocaleDateString('tr-TR', { 
                        day: 'numeric',
                        month: 'long'
                    })
                };
            }),
            dateRange: formatDateRange(monday, true)
        };
    };

    // Update fetchCurrentWeekEvents
    const fetchCurrentWeekEvents = async (offset: number = 0) => {
        try {
            const { days, dateRange } = getCurrentWeekDays(offset);
            setCurrentDateRange(dateRange);
            setCurrentWeekDays(days);
            
            const eventTypeMap = new Map<string, { [key: string]: number }>();

            // Initialize with all event types even if there's no data
            const defaultEventTypes = [
                'Terörizm (TE)',
                'Örgütlü Suçlar (OS)',
                'Sosyal ve Politik İstikrarsızlık (SP)',
                'Çevresel ve Doğal Afetler (CA)',
                'Bilgi Güvenliği ve Enformasyon Tehditleri (BG)',
                'Bireysel ve Adi Suçlar (BA)',
                'Göç ve Mülteci Krizleri (GM)',
                'Sağlık ve Biyolojik Tehditler (SB)',
                'Yasal ve Düzenleyici Uyumsuzluklar (YD)',
                'Ekonomik ve Finansal Tehditler (EF)'
            ];

            // Initialize map with all event types and zero counts
            defaultEventTypes.forEach(eventType => {
                const dailyCounts: { [key: string]: number } = {};
                days.forEach(day => {
                    dailyCounts[day.name] = 0;
                });
                eventTypeMap.set(eventType, dailyCounts);
            });

            // Rest of the function remains the same...
            const dailyResponses = await Promise.all(
                days.map(day => 
                    EventsApi.getEventsCounts({
                        dateStart: day.date,
                        dateEnd: day.date
                    }) as Promise<EventCountsResponse>
                )
            );

            // Update counts with actual data
            dailyResponses.forEach((response, index) => {
                const dayName = days[index].name;
                response.eventTypeCounts?.forEach((count) => {
                    const eventTypeCounts = eventTypeMap.get(count.eventType);
                    if (eventTypeCounts) {
                        eventTypeCounts[dayName] = count.count;
                    }
                });
            });

            // Convert to array and update state
            const formattedData = Array.from(eventTypeMap.entries()).map(([eventType, dailyCounts]) => ({
                eventType,
                dailyCounts
            }));

            setWeeklyEventTypeCounts(formattedData);
        } catch (error) {
            console.error('Haftalık olay sayıları alınırken hata:', error);
            setWeeklyEventTypeCounts([]);
        }
    };

    // Add navigation handlers
    const handlePreviousWeek = () => {
        if (weekOffset < MAX_WEEK_OFFSET) {
            const newOffset = weekOffset + 1;
            setWeekOffset(newOffset);
            fetchCurrentWeekEvents(newOffset);
        }
    };

    const handleNextWeek = () => {
        if (weekOffset > 0) {
            const newOffset = weekOffset - 1;
            setWeekOffset(newOffset);
            fetchCurrentWeekEvents(newOffset);
        }
    };

    // Add new function to fetch risk level for a pin
    const fetchPinRiskLevel = async (pinId: string) => {
        try {
            const response = await EventsApi.getPinFullReport(pinId);
            const riskAnswer = response.riskAnswers.find(
                answer => answer.isAfterMeasures && 
                answer.categoryName === "RİSK VE TEHDİT DEĞERLENDİRME"
            );
            return riskAnswer?.residualRiskLevel || null;
        } catch (error) {
            console.error(`Error fetching risk data for pin ${pinId}:`, error);
            return null;
        }
    };

    // Modify the fetchPins function
    const fetchPins = async () => {
        try {
            const response = await EventsApi.getPins() as PinResponse;
            if (response?.items) {
                setPins(response.items);
                
                // Fetch risk data for each pin
                const riskData: PinRiskData = {};
                await Promise.all(
                    response.items.map(async (pin) => {
                        const riskLevel = await fetchPinRiskLevel(pin.id);
                        riskData[pin.id] = riskLevel;
                    })
                );
                setPinRiskLevels(riskData);
            }
        } catch (error) {
            console.error("Pin verisi alınırken hata:", error);
        }
    };

    const fetchLastSixWeeksEvents = async () => {
        try {
            const endDate = new Date();
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - (6 * 7));

            const response = await EventsApi.getEvents({
                dateStart: format(startDate, 'yyyy-MM-dd'),
                dateEnd: format(endDate, 'yyyy-MM-dd')
            }) as EventResponse;

            const events = response.events.items
                .filter(event => 
                    event.city && 
                    !event.city.toLowerCase().includes('unknown')
                )
                .map(event => ({
                    city: event.city,
                    description: event.description
                }));

            const correlationMatrix = createCorrelationMatrix(events);
            setCorrelationData(correlationMatrix); // Korelasyon matrisini state'e kaydedelim

            const significantCorrelations: { cities: string; correlation: number; }[] = [];
            desiredCities.forEach(city1 => {
                desiredCities.forEach(city2 => {
                    if (city1 < city2 && correlationMatrix[city1][city2] > 0) {
                        significantCorrelations.push({
                            cities: `${city1} - ${city2}`,
                            correlation: correlationMatrix[city1][city2]
                        });
                    }
                });
            });


        } catch (error) {
            console.error('Error fetching events:', error);
        }
    };

    useEffect(() => {
        setIsLoading(true);
        
        // Hemen olayları çekelim
        fetchLastSixWeeksEvents();

        // Son 6 haftanın tarih aralıklarını hesapla
        const getWeekRanges = () => {
            const ranges = [];
            const today = new Date();
            
            // Get to current week's Monday
            const currentDay = today.getDay();
            const diff = currentDay === 0 ? 6 : currentDay - 1;
            today.setDate(today.getDate() - diff - 7); // Son haftayı dahil etmemek için -7 ekliyoruz
            
            for (let i = 5; i >= 0; i--) {
                const weekStart = new Date(today);
                weekStart.setDate(today.getDate() - (i * 7));
                
                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekEnd.getDate() + 6);

                const startStr = weekStart.toLocaleDateString('tr-TR', { 
                    day: 'numeric', 
                    month: 'long'
                });
                const endStr = weekEnd.toLocaleDateString('tr-TR', { 
                    day: 'numeric', 
                    month: 'long'
                });
                
                ranges.push({
                    dateStart: weekStart.toISOString().split('T')[0],
                    dateEnd: weekEnd.toISOString().split('T')[0],
                    label: `${startStr}-${endStr}`
                });
            }
            return ranges;
        };

        const weekRanges = getWeekRanges();

        // Sadece sol üst grafik için veri çekme
        Promise.all(
            weekRanges.map(({ dateStart, dateEnd }) =>
                EventsApi.getEventsCounts({ dateStart, dateEnd })
                    .then((response: any) => ({
                        weekStart: dateStart,
                        eventTypeCounts: response.eventTypeCounts || []
                    }))
            )
        ).then((results) => {
            const allEventTypes = new Set<string>();
            results.forEach(week => {
                week.eventTypeCounts.forEach((count: { eventType: string; }) => {
                    allEventTypes.add(count.eventType);
                });
            });

            const series = Array.from(allEventTypes).map(eventType => ({
                data: results.map(week => {
                    const eventData = week.eventTypeCounts.find(
                        (count: { eventType: string; }) => count.eventType === eventType
                    );
                    return eventData?.count || 0;
                }),
                label: eventType,
                color: eventTypeColors[eventType],
                valueFormatter: (value: number) => Math.round(value).toString(),
                showMark: true,
                lineStyle: { strokeWidth: 2 },
                markerSize: 5
            }));

            setWeeklyEventData({
                series,
                xAxis: weekRanges.map(range => range.label)
            });
            setIsLoading(false);
        }).catch((error) => {
            console.error("API Error:", error);
            setIsLoading(false);
        });

        fetchPins();
        fetchCurrentWeekEvents(weekOffset);
        // eslint-disable-next-line
    }, []);

    // Veriler yüklenene kadar loading göster
    if (isLoading) {
        return (
            			<LogoSpinner size={60} minHeight="100vh" />
        );
    }

    return (
        <Box sx={{ 
            flexGrow: 1, 
            padding: '1rem',
            width: '100%',
            boxSizing: 'border-box',
            bgcolor: reportUi.pageBg,
        }}>
            <Grid container spacing={3} sx={{ minHeight: '100vh' }}>
                {/* Sol Üst Alan - Grafik */}
                <Grid item xs={12} md={6} sx={{ height: '400px' }}> {/* Sabit yükseklik */}
                    <Paper elevation={0} sx={{ 
                        height: '100%', 
                        padding: '1rem', 
                        display: 'flex',
                        flexDirection: 'column', 
                        alignItems: 'center',
                        overflow: 'hidden',
                        bgcolor: reportUi.surface,
                        border: `1px solid ${reportUi.border}`,
                        borderRadius: reportUi.radius,
                        boxShadow: 'none',
                    }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ color: reportUi.textMain, fontWeight: 700 }}>
                            Tüm Türkiye için Son 6 Hafta Olay Türü Dağılımı
                        </Typography>
                        {weeklyEventData.series.length > 0 ? (
                            <>
                                <CustomLegend series={weeklyEventData.series} />
                                <Box sx={{ flexGrow: 1, width: '100%', position: 'relative' }}>
                                    <LineChart
                                        sx={{ 
                                            width: '100%',
                                            height: '100%',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            '& .MuiChartsAxis-tickLabel': {
                                                transform: 'translateY(10px)',
                                            }
                                        }}
                                        series={weeklyEventData.series}
                                        xAxis={[{
                                            data: weeklyEventData.xAxis,
                                            scaleType: 'point',
                                            tickLabelStyle: {
                                                angle: 0,
                                                textAnchor: 'middle',
                                                fontSize: 10 // Yazı boyutunu küçülttüm
                                            },
                                            tickSize: 0,
                                            valueFormatter: (value) => {
                                                const parts = value.split('-');
                                                return parts.join('\n');
                                            }
                                        }]}
                                        yAxis={[{
                                            min: 0,
                                            tickMinStep: 1,
                                            position: 'left',
                                            tickSize: 5,
                                            tickLabelStyle: {
                                                textAnchor: 'end',
                                                fontSize: 10 // Yazı boyutunu küçülttüm
                                            },
                                            valueFormatter: (value) => Math.round(value).toString()
                                        }]}
                                        margin={{ 
                                            top: 10, 
                                            bottom: 60, // Alt marjini azalttım
                                            left: 50, // Sol marjini azalttım
                                            right: 20
                                        }}
                                        legend={{ hidden: true }}
                                        disableAxisListener
                                    />
                                </Box>
                            </>
                        ) : (
                            <Typography>Veri bulunamadı</Typography>
                        )}
                    </Paper>
                </Grid>

                {/* Sağ Üst Alan - Tablo */}
                <Grid item xs={12} md={6} sx={{ height: '400px' }}> {/* Sabit yükseklik */}
                    <Paper elevation={0} sx={{ 
                        height: '100%', 
                        padding: '1rem', 
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        bgcolor: reportUi.surface,
                        border: `1px solid ${reportUi.border}`,
                        borderRadius: reportUi.radius,
                        boxShadow: 'none',
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            gap: 2,
                            mb: 1
                        }}>
                            <IconButton 
                                onClick={handlePreviousWeek}
                                disabled={weekOffset >= MAX_WEEK_OFFSET}
                                size="small"
                                sx={{ color: reportUi.primary }}
                            >
                                <ArrowBackIosNew fontSize="small" />
                            </IconButton>
                            <Typography variant="subtitle1" component="div" sx={{ color: reportUi.textMain, fontWeight: 700 }}>
                                Günlük Olay Sayıları {currentDateRange}
                            </Typography>
                            <IconButton 
                                onClick={handleNextWeek}
                                disabled={weekOffset <= 0}
                                size="small"
                                sx={{ color: reportUi.primary }}
                            >
                                <ArrowForwardIos fontSize="small" />
                            </IconButton>
                        </Box>
                        <Box sx={{ 
                            flexGrow: 1, 
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <TableContainer sx={{ 
                                flexGrow: 1,
                                overflow: 'auto',
                                border: `1px solid ${reportUi.border}`,
                                borderRadius: reportUi.radius,
                            }}>
                                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell 
                                                sx={{ 
                                                    fontWeight: 'bold', 
                                                    backgroundColor: reportUi.headerBg,
                                                    color: '#fff',
                                                    position: 'sticky',
                                                    left: 0,
                                                    zIndex: 2,
                                                    width: '30%' // Sütun genişliğini ayarlıyorum
                                                }}
                                            >
                                                Olay Türü
                                            </TableCell>
                                            {currentWeekDays.map((day) => (
                                                <TableCell 
                                                    key={day.name} 
                                                    align="center"
                                                    sx={{ 
                                                        fontWeight: 'bold', 
                                                        backgroundColor: reportUi.headerBg,
                                                        color: '#fff',
                                                        whiteSpace: 'pre-line',
                                                        padding: '4px 2px', // Padding'i azaltıyorum
                                                        minWidth: '70px', // Sütun genişliğini azaltıyorum
                                                    }}
                                                >
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            fontWeight: 'bold',
                                                            marginBottom: '2px',
                                                            color: '#fff',
                                                        }}
                                                    >
                                                        {day.name}
                                                    </Typography>
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            fontStyle: 'italic',
                                                            color: reportUi.headerSubtext,
                                                            fontSize: '0.65rem'
                                                        }}
                                                    >
                                                        {day.displayDate}
                                                    </Typography>
                                                </TableCell>
                                            ))}
                                            <TableCell 
                                                align="center"
                                                sx={{ 
                                                    fontWeight: 'bold', 
                                                    backgroundColor: reportUi.headerBg,
                                                    color: '#fff',
                                                    width: '10%' // Sütun genişliğini ayarlıyorum
                                                }}
                                            >
                                                Toplam
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {weeklyEventTypeCounts.map((row) => {
                                            const total = Object.values(row.dailyCounts).reduce((sum, count) => sum + count, 0);
                                            return (
                                                <TableRow key={row.eventType}>
                                                    <TableCell 
                                                        sx={{ 
                                                            position: 'sticky',
                                                            left: 0,
                                                            backgroundColor: reportUi.surface,
                                                            zIndex: 1
                                                        }}
                                                    >
                                                        {row.eventType}
                                                    </TableCell>
                                                    {Object.values(row.dailyCounts).map((count, index) => (
                                                        <TableCell 
                                                            key={index} 
                                                            align="center"
                                                        >
                                                            {count}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell 
                                                        align="center"
                                                        sx={{ 
                                                            fontWeight: 'bold',
                                                            backgroundColor: reportUi.surfaceSoft,
                                                            color: reportUi.textMain,
                                                        }}
                                                    >
                                                        {total}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Paper>
                </Grid>

                {/* Bottom Area - Full Width for Chord Diagram */}
                <Grid item xs={12} sx={{ height: '65%' }}>
                    <Paper elevation={0} sx={{ height: '100%', bgcolor: reportUi.surface, border: `1px solid ${reportUi.border}`, borderRadius: reportUi.radius, boxShadow: 'none', overflow: 'hidden' }}>
                        <ChordDiagram correlationData={correlationData} />
                    </Paper>
                </Grid>
            </Grid>

            
        </Box>
    );
};

export default Analysis;